import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";
import { GeneratedPost, Platform } from "../types";

export interface Draft {
  id: string;
  title: string;
  posts: GeneratedPost[];
  contentData: any;
  platforms: Platform[];
  mediaUrl?: string;
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const DRAFTS_STORAGE_KEY = "omnishare_drafts";

const generateDraftId = (): string => {
  return `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

type DraftsState = {
  drafts: Draft[];
  loadDrafts: () => void;
  getDraftById: (id: string) => Draft | null;
  saveDraftPost: (posts: GeneratedPost[], contentData: any, title?: string, draftId?: string) => Draft;
  updateDraft: (
    id: string,
    posts: GeneratedPost[],
    contentData?: any,
    title?: string
  ) => Draft | null;
  deleteDraft: (id: string) => boolean;
  clearAllDrafts: () => void;
  getDraftsCount: () => number;
};

const customStorage: PersistStorage<DraftsState> = {
  getItem: (name) => {
    const item = localStorage.getItem(name);
    return item ? JSON.parse(item) : null;
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
  },
};

export const useDraftStore = create<DraftsState>()(
  persist(
    (set, get) => ({
      drafts: [],
      loadDrafts: () => {
        try {
          const raw = localStorage.getItem(DRAFTS_STORAGE_KEY);
          const drafts: Draft[] = raw ? JSON.parse(raw) : [];
          drafts.sort(
            (a: Draft, b: Draft) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
          set({ drafts });
        } catch (error) {
          console.error("Error loading drafts:", error);
          set({ drafts: [] });
        }
      },
      getDraftById: (id: string) => {
        const drafts = get().drafts;
        return drafts.find((d) => d.id === id) || null;
      },
      saveDraftPost: (posts: GeneratedPost[], contentData: any, title?: string, draftId?: string) => {
        const now = new Date().toISOString();
        const platforms = posts.map((p) => p.platform);
        const draftTitle =
          title || posts[0]?.caption?.substring(0, 50) + "..." || `Draft`;

        // Normalize posts to ensure mediaUrl is present when mediaAssets is used
        const normalizedPosts = posts.map((p) => {
          const mediaFromAssets = (p as any)?.mediaAssets?.[0];
          let extractedMediaUrl: string | undefined;
          if (p?.mediaUrl) extractedMediaUrl = p.mediaUrl;
          else if (p?.imageUrl) extractedMediaUrl = p.imageUrl;
          else if (mediaFromAssets) {
            if (typeof mediaFromAssets === "string") extractedMediaUrl = mediaFromAssets;
            else if (mediaFromAssets?.url) extractedMediaUrl = mediaFromAssets.url;
          }

          return {
            ...p,
            mediaUrl: extractedMediaUrl || p.mediaUrl || p.imageUrl || undefined,
            imageUrl: p.imageUrl || extractedMediaUrl || p.mediaUrl || undefined,
            thumbnailUrl: p.thumbnailUrl || ((p as any)?.mediaAssets?.[0]?.thumbnailUrl ?? undefined),
          };
        });

        const mediaUrl =
          normalizedPosts[0]?.mediaUrl || contentData?.mediaUrl || contentData?.serverUrl;

        const thumbnailUrl = normalizedPosts[0]?.thumbnailUrl || contentData?.thumbnailUrl;

        const newDraft: Draft = {
          id: draftId || generateDraftId(),
          title: draftTitle,
          posts: normalizedPosts,
          contentData,
          platforms,
          mediaUrl,
          thumbnailUrl,
          createdAt: now,
          updatedAt: now,
        };

        const drafts = [newDraft, ...get().drafts];
        set({ drafts });
        return newDraft;
      },
      updateDraft: (id, posts, contentData, title) => {
        const drafts = get().drafts.slice();
        const idx = drafts.findIndex((d) => d.id === id);
        if (idx === -1) return null;

        const existing = drafts[idx];
        const platforms = posts.map((p) => p.platform);

        // Normalize posts for update as well
        const normalizedPosts = posts.map((p) => {
          const mediaFromAssets = (p as any)?.mediaAssets?.[0];
          let extractedMediaUrl: string | undefined;
          if (p?.mediaUrl) extractedMediaUrl = p.mediaUrl;
          else if (p?.imageUrl) extractedMediaUrl = p.imageUrl;
          else if (mediaFromAssets) {
            if (typeof mediaFromAssets === "string") extractedMediaUrl = mediaFromAssets;
            else if (mediaFromAssets?.url) extractedMediaUrl = mediaFromAssets.url;
          }

          return {
            ...p,
            mediaUrl: extractedMediaUrl || p.mediaUrl || p.imageUrl || undefined,
            imageUrl: p.imageUrl || extractedMediaUrl || p.mediaUrl || undefined,
            thumbnailUrl: p.thumbnailUrl || ((p as any)?.mediaAssets?.[0]?.thumbnailUrl ?? undefined),
          };
        });

        const mediaUrl =
          normalizedPosts[0]?.mediaUrl || normalizedPosts[0]?.imageUrl || contentData?.mediaUrl || contentData?.serverUrl || existing.mediaUrl;

        const thumbnailUrl = normalizedPosts[0]?.thumbnailUrl || contentData?.thumbnailUrl || existing.thumbnailUrl;

        const updated: Draft = {
          ...existing,
          posts: normalizedPosts,
          contentData: contentData || existing.contentData,
          platforms,
          mediaUrl,
          thumbnailUrl,
          title: title || existing.title,
          updatedAt: new Date().toISOString(),
        };

        drafts[idx] = updated;
        // keep newest first
        drafts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        set({ drafts });
        return updated;
      },
      deleteDraft: (id: string) => {
        const drafts = get().drafts;
        const filtered = drafts.filter((d) => d.id !== id);
        if (filtered.length === drafts.length) return false;
        set({ drafts: filtered });
        return true;
      },
      clearAllDrafts: () => set({ drafts: [] }),
      getDraftsCount: () => get().drafts.length,
    }),
    {
      name: DRAFTS_STORAGE_KEY,
      storage: customStorage,
    }
  )
);

// Backwards-compatible named exports (original API)
export const getDrafts = (): Draft[] => useDraftStore.getState().drafts;
export const getDraftById = (id: string): Draft | null => useDraftStore.getState().getDraftById(id);
export const saveDraft = (posts: GeneratedPost[], contentData: any, title?: string) =>
  useDraftStore.getState().saveDraftPost(posts, contentData, title);
export const saveDraftPost = (posts: GeneratedPost[], contentData: any, title?: string, draftId?: string) =>
  useDraftStore.getState().saveDraftPost(posts, contentData, title, draftId);
export const updateDraft = (
  id: string,
  posts: GeneratedPost[],
  contentData?: any,
  title?: string
): Draft | null => useDraftStore.getState().updateDraft(id, posts, contentData, title);
export const deleteDraft = (id: string): boolean => useDraftStore.getState().deleteDraft(id);
export const clearAllDrafts = (): void => useDraftStore.getState().clearAllDrafts();
export const getDraftsCount = (): number => useDraftStore.getState().getDraftsCount();
